import { useEffect, useMemo, useState } from "react";
import { ApiError, fetchDashboardSummary, type DashboardLeaderboardRowDto, type DashboardSummaryDto } from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

const WINDOWS = [30, 60, 90] as const;
const SMALL_N_THRESHOLD = 30;

function fmtMetric(name: string | null, value: number | null): string {
  if (name === null || value === null) return "—";
  if (name === "vol_error_qlike") return value.toFixed(3);
  return `${value >= 0 ? "+" : ""}${value.toFixed(4)}`;
}

type Level = "all" | "index" | "stock";

export function DashboardPage({
  token,
  onUnauthorized,
  onSelectModel,
}: {
  token: string;
  onUnauthorized: () => void;
  onSelectModel: (modelId: string, version: string) => void;
}) {
  const [window_, setWindow] = useState<number>(60);
  const [level, setLevel] = useState<Level>("all");
  const [data, setData] = useState<DashboardSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchDashboardSummary(token, window_)
      .then(setData)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorized();
          return;
        }
        setError(err instanceof ApiError ? err.message : "request failed");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, window_]);

  const rows = useMemo(() => {
    if (!data) return [];
    let r = data.leaderboard;
    if (level !== "all") r = r.filter((m) => m.level === level);
    return [...r].sort((a, b) => (b.metric_value ?? -Infinity) - (a.metric_value ?? -Infinity));
  }, [data, level]);

  const { sorted: sortedRows, sortKey, sortDir, toggleSort } = useSort<DashboardLeaderboardRowDto>(
    rows,
    (row, key) => (row as unknown as Record<string, unknown>)[key],
  );

  function exportCsv() {
    if (!data) return;
    exportRowsAsCsv(
      data.leaderboard as unknown as Record<string, unknown>[],
      [
        { key: "model_id", label: "model_id" },
        { key: "level", label: "level" },
        { key: "target_ref", label: "target_ref" },
        { key: "slot_key", label: "slot_key" },
        { key: "output_type", label: "output_type" },
        { key: "metric_name", label: "metric_name" },
        { key: "metric_value", label: "metric_value" },
        { key: "sample_n", label: "sample_n" },
        { key: "pending_n", label: "pending_n" },
        { key: "is_champion", label: "is_champion" },
        { key: "monitored_not_raced", label: "monitored_not_raced" },
      ],
      "model_leaderboard.csv",
    );
  }

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;
  if (!data) return <div className="page-sub">{loading ? "Loading…" : ""}</div>;

  const k = data.kpis;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Model Overview</h1>
          <div className="page-sub">
            The factory floor, live. Every champion's real-world track record, rolled up by level — how the whole
            system is actually performing.
          </div>
        </div>
        <div className="asof-control">
          <span className="note">window:</span>
          <div style={{ display: "inline-flex", border: "1px solid var(--rule-strong)", borderRadius: 7, overflow: "hidden" }}>
            {WINDOWS.map((w) => (
              <button
                key={w}
                className={`app-nav-tab${window_ === w ? " active" : ""}`}
                style={{ padding: "5px 10px", fontSize: "0.72rem", borderRadius: 0 }}
                onClick={() => setWindow(w)}
              >
                {w}d
              </button>
            ))}
          </div>
        </div>
      </div>

      <PageHelper>
        Every live champion's real-world track record, rolled up by level. Live metric = <FieldHelp term="ic" /> for return/σ
        models or <FieldHelp term="qlike" /> for the monitored index-σ convention. Click any row for that model's full history.
      </PageHelper>

      <div className="kpis">
        <div className="kpi">
          <div className="l">Active champions</div>
          <div className="v">{k.active_champions}</div>
          <div className="s">{k.active_champions} slots live</div>
        </div>
        <div className="kpi">
          <div className="l">Avg live rank-IC (stock return)</div>
          <div className="v">{k.avg_stock_return_ic !== null ? fmtMetric("IC", k.avg_stock_return_ic) : "—"}</div>
          <div className="s">return champions, {window_}d window</div>
        </div>
        <div className="kpi">
          <div className="l">Avg index σ QLIKE</div>
          <div className="v">{k.avg_index_sigma_qlike !== null ? fmtMetric("vol_error_qlike", k.avg_index_sigma_qlike) : "—"}</div>
          <div className="s">monitored, not raced</div>
        </div>
        <div className="kpi">
          <div className="l">Resolved predictions</div>
          <div className="v">{(k.total_resolved / 1000).toFixed(1)}k</div>
          <div className="s">{window_}d window, across all champions</div>
        </div>
        <div className="kpi">
          <div className="l">Open challenges</div>
          <div className="v" style={{ color: k.open_challenges > 0 ? "var(--bad)" : undefined }}>{k.open_challenges}</div>
          <div className="s">{k.challenges_note}</div>
        </div>
      </div>

      <div className="card">
        <div className="h3">
          <span>Live leaderboard <span className="note">click a model for its full track record · sample_n shown next to every metric</span></span>
          <button className="btn ghost sm" onClick={exportCsv}>↓ CSV</button>
        </div>
        <div className="levtabs">
          {(["all", "index", "stock"] as Level[]).map((l) => (
            <button key={l} className={level === l ? "on" : undefined} onClick={() => setLevel(l)}>
              {l === "all" ? "All levels" : l === "index" ? "Indices" : "Stock segments"}
            </button>
          ))}
        </div>
        <div className="table-wrap">
          <table className="stocks-table">
            <thead>
              <tr>
                <th className="l clickable" onClick={() => toggleSort("model_id")}>Model {sortGlyph("model_id", sortKey, sortDir)}</th>
                <th className="clickable" onClick={() => toggleSort("level")}>Level {sortGlyph("level", sortKey, sortDir)}</th>
                <th className="l clickable" onClick={() => toggleSort("slot_key")}>Slot <FieldHelp term="slot" /> {sortGlyph("slot_key", sortKey, sortDir)}</th>
                <th className="r clickable" onClick={() => toggleSort("metric_value")}>Live metric <FieldHelp term="ic" /> {sortGlyph("metric_value", sortKey, sortDir)}</th>
                <th className="r clickable" onClick={() => toggleSort("sample_n")}>Resolved n <FieldHelp term="sample_n" /> {sortGlyph("sample_n", sortKey, sortDir)}</th>
                <th>Monitoring <FieldHelp term="monitored_not_raced" /></th>
                <th>Status <FieldHelp term="champion_challenger" /></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((m: DashboardLeaderboardRowDto) => (
                <tr key={`${m.model_id}:${m.version}`} className="clickable" style={{ cursor: "pointer" }} onClick={() => onSelectModel(m.model_id, m.version)}>
                  <td className="l" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{m.model_id}</td>
                  <td><span className="chip">{m.level}</span></td>
                  <td className="l mono" style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>{m.slot_key}</td>
                  <td className="r mono" style={{ fontWeight: 600 }}>{fmtMetric(m.metric_name, m.metric_value)}</td>
                  <td className="r">
                    <span className="mono">{(m.sample_n ?? 0).toLocaleString()}</span>
                    {m.sample_n !== null && m.sample_n < SMALL_N_THRESHOLD && (
                      <span className="gaps-badge" style={{ marginLeft: 6 }} title="Too few resolved predictions to treat this as a verdict">small-n</span>
                    )}
                  </td>
                  <td>
                    {m.monitored_not_raced ? <span className="chip">monitored, not raced</span> : <span className="chip st-champion">raced</span>}
                  </td>
                  <td><span className={`chip ${m.is_champion ? "st-champion" : "st-challenger"}`}>{m.is_champion ? "champion" : "challenger"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="h3"><span>Decay watch <span className="note">backtest-vs-live shortfall, per champion</span></span></div>
        {data.decay_watch_available ? null : (
          <div className="empty-state">
            <div className="headline">Not available</div>
            <div>{data.decay_watch_note}</div>
          </div>
        )}
      </div>

      <div className="foot-note">
        Rollup by level ({window_}d window): {data.level_rollups.map((r) => `${r.level}/${r.metric_name}: ${r.n_models} models, mean ${r.mean_metric_value?.toFixed(4) ?? "—"}, ${r.total_sample_n.toLocaleString()} resolved`).join(" · ")}
      </div>
    </>
  );
}
